import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

import type { useMatchFlow } from "@/features/match/useMatchFlow";
import { useTranslations } from "@/i18n";
import { useActivityList, usePlayerList } from "@/store/selectors";
import { ActivityFormat } from "@/types";
import { theme } from "@/ui/theme";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StageTag = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: ${theme.colors.primary};
`;

const Title = styled.h2`
  font-family: ${theme.fonts.display};
  font-size: 2.25rem;
  line-height: 0.95;
  text-wrap: balance;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
  margin-top: 0.5rem;
`;

const List = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CardBtn = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  width: 100%;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: ${theme.radii["2xl"]};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgCard} 70%, transparent);
  box-shadow: ${theme.shadow.card};
  backdrop-filter: blur(8px);
  text-align: left;
  transition: all 0.15s;

  &:hover {
    border-color: color-mix(in oklab, ${theme.colors.primary} 50%, transparent);
    background: ${theme.colors.bgCard};
  }

  &:active {
    transform: scale(0.99);
  }
`;

const IconBox = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: ${theme.radii.xl};
  background: ${theme.colors.secondary};
  font-size: 1.75rem;
  border: 1px solid ${theme.colors.border};
`;

const CardBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardTitle = styled.h3`
  font-family: ${theme.fonts.display};
  font-size: 1.25rem;
`;

const CardTagline = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FORMAT_EMOJI: Record<ActivityFormat, string> = {
  [ActivityFormat.FreeForAll]: "👤",
  [ActivityFormat.Teams]: "👥",
  [ActivityFormat.Tournament]: "🏟️",
};

const FORMAT_LABEL_KEY: Record<
  ActivityFormat,
  "arena.formatFfa" | "arena.formatTeams" | "arena.formatTournament"
> = {
  [ActivityFormat.FreeForAll]: "arena.formatFfa",
  [ActivityFormat.Teams]: "arena.formatTeams",
  [ActivityFormat.Tournament]: "arena.formatTournament",
};

const chevronCss = css`
  width: 1.25rem;
  height: 1.25rem;
  color: ${theme.colors.textMuted};
  transition: transform 0.15s;

  button:hover & {
    transform: translateX(4px);
    color: ${theme.colors.primary};
  }
`;

const EmptyState = styled.p`
  text-align: center;
  color: ${theme.colors.textMuted};
  padding: 2rem 1rem;
  font-size: 0.875rem;
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.radii["2xl"]};
`;

type FlowApi = ReturnType<typeof useMatchFlow>;

export const ActivityStage = ({ flow }: { flow: FlowApi }) => {
  const { t } = useTranslations();
  const activities = useActivityList();
  const players = usePlayerList();

  if (players.length < 2) {
    return (
      <Wrap>
        <header>
          <StageTag>{t("arena.stagePickGame")}</StageTag>
          <Title>{t("arena.whatPlaying")}</Title>
        </header>
        <EmptyState>{t("arena.needPlayersArena")}</EmptyState>
      </Wrap>
    );
  }

  if (activities.length === 0) {
    return (
      <Wrap>
        <header>
          <StageTag>{t("arena.stagePickGame")}</StageTag>
          <Title>{t("arena.whatPlaying")}</Title>
        </header>
        <EmptyState>{t("arena.needActivitiesArena")}</EmptyState>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <header>
        <StageTag>{t("arena.stagePickGame")}</StageTag>
        <Title>{t("arena.whatPlaying")}</Title>
        <Subtitle>{t("arena.tapToStart")}</Subtitle>
      </header>

      <List>
        {activities.map((a, i) => (
          <motion.li
            key={a.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: i * 0.04,
              type: "spring",
              stiffness: 260,
              damping: 24,
            }}
            style={{ display: "flex" }}
          >
            <CardBtn onClick={() => flow.selectActivity(a.id)}>
              <IconBox>{FORMAT_EMOJI[a.format]}</IconBox>
              <CardBody>
                <CardTitle>{a.name}</CardTitle>
                <CardTagline>{t(FORMAT_LABEL_KEY[a.format])}</CardTagline>
              </CardBody>
              <ChevronRight css={chevronCss} />
            </CardBtn>
          </motion.li>
        ))}
      </List>
    </Wrap>
  );
};
